/**
 * Fullscreen GPU passes used by TextureFactory.
 * owner: materials
 */

export const FS_VERT = /* glsl */`
precision highp float;
in vec3 position;
in vec2 uv;
out vec2 vUv;
void main(){
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Pass A: evaluate the procedural surface into two half-float MRT attachments. */
export const SYNTH_FRAG_HEAD = /* glsl */`
precision highp float;
precision highp int;
in vec2 vUv;
layout(location = 0) out vec4 oAlbedo;   // rgb = linear albedo, a = coverage
layout(location = 1) out vec4 oOrmh;     // r = AO, g = roughness, b = metalness, a = height
`;

export const SYNTH_FRAG_MAIN = /* glsl */`
void main(){
  Surf s;
  s.albedo = vec3(0.5);
  s.rough  = 0.8;
  s.metal  = 0.0;
  s.ao     = 1.0;
  s.height = 0.5;
  s.alpha  = 1.0;
  surface(vUv, s);
  oAlbedo = vec4(s.albedo, s.alpha);
  oOrmh   = vec4(s.ao, s.rough, s.metal, s.height);
}
`;

/**
 * Pass B: resolve into the three 8-bit maps the renderer actually samples.
 *  - albedo is sRGB-encoded (texture.colorSpace = SRGBColorSpace)
 *  - ORM stays linear
 *  - a tangent-space normal is derived from the height field with a Sobel kernel
 *  - a cheap horizon-style AO is derived from the same height field and folded in
 */
export const RESOLVE_FRAG = /* glsl */`
precision highp float;
in vec2 vUv;

uniform sampler2D uAlbedo;
uniform sampler2D uOrmh;
uniform vec2  uTexel;
uniform float uNormalStrength;
uniform float uAoStrength;
uniform float uHeightScale;
uniform float uHeightContrast;
uniform float uPackRoughInNormalAlpha;

layout(location = 0) out vec4 oAlbedo;
layout(location = 1) out vec4 oOrm;
layout(location = 2) out vec4 oNormal;

float H(vec2 p){ return textureLod(uOrmh, p, 0.0).a; }

void main(){
  vec4 a = textureLod(uAlbedo, vUv, 0.0);
  vec4 o = textureLod(uOrmh, vUv, 0.0);

  vec2 t = uTexel;
  float h00 = H(vUv + vec2(-t.x, -t.y));
  float h10 = H(vUv + vec2( 0.0, -t.y));
  float h20 = H(vUv + vec2( t.x, -t.y));
  float h01 = H(vUv + vec2(-t.x,  0.0));
  float h11 = H(vUv);
  float h21 = H(vUv + vec2( t.x,  0.0));
  float h02 = H(vUv + vec2(-t.x,  t.y));
  float h12 = H(vUv + vec2( 0.0,  t.y));
  float h22 = H(vUv + vec2( t.x,  t.y));

  float gx = (h20 + 2.0 * h21 + h22) - (h00 + 2.0 * h01 + h02);
  float gy = (h02 + 2.0 * h12 + h22) - (h00 + 2.0 * h10 + h20);

  // Sobel is 8x the average gradient per texel; convert to a slope in UV space so the
  // result is resolution independent, then scale to taste.
  float sx = gx / (8.0 * t.x) * uHeightScale * uNormalStrength;
  float sy = gy / (8.0 * t.y) * uHeightScale * uNormalStrength;
  vec3 n = normalize(vec3(-sx, -sy, 1.0));

  // cavity AO from a wide height comparison (4 radii, 8 taps each would be costly —
  // use 2 radii x 8 taps, which is plenty for a baked map)
  float occ = 0.0;
  float wsum = 0.0;
  for (int i = 0; i < 8; i++){
    float ang = float(i) * 0.7853981634;
    vec2 d = vec2(cos(ang), sin(ang));
    for (int r = 1; r <= 3; r++){
      float rad = float(r) * 2.5;
      float hs = H(vUv + d * t * rad);
      float w = 1.0 / rad;
      occ += max(0.0, hs - h11) * w;
      wsum += w;
    }
  }
  occ /= max(wsum, 1e-4);
  float ao = 1.0 - clamp(occ * 6.0 * uAoStrength, 0.0, 0.85);

  // attachment 0 is an SRGB8_ALPHA8 target: write linear, the framebuffer encodes.
  // POM wants height = 1 at the outermost surface and 0 at the deepest pit. The surface
  // programs are authored around a mid-grey datum, so renormalise against the field's own
  // mean (the top mip of the scratch target) — otherwise parallax degenerates into a
  // constant UV offset with almost no relief.
  float meanH = textureLod(uOrmh, vec2(0.5), 20.0).a;
  float hOut = clamp((o.a - meanH) * uHeightContrast + 0.88, 0.0, 1.0);

  oAlbedo = vec4(max(a.rgb, vec3(0.0)), a.a);
  oOrm    = vec4(clamp(o.r * ao, 0.0, 1.0), clamp(o.g, 0.0, 1.0), clamp(o.b, 0.0, 1.0), 1.0);
  oNormal = vec4(n * 0.5 + 0.5, mix(hOut, o.g, uPackRoughInNormalAlpha));
}
`;

/**
 * Blue noise.
 *  pass 0 : white noise (4 decorrelated channels)
 *  pass N : high-pass against a wrapped gaussian, then renormalise through the local
 *           gaussian CDF so the histogram stays uniform. Iterating this pushes the
 *           spectrum's energy to the high frequencies — a cheap, deterministic,
 *           fully-GPU stand-in for void-and-cluster.
 */
export const BLUENOISE_SEED_FRAG = /* glsl */`
precision highp float;
in vec2 vUv;
uniform float uSize;
layout(location = 0) out vec4 oCol;

float hash(vec3 p){
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.x + p.y) * p.z);
}
void main(){
  vec2 c = floor(vUv * uSize);
  oCol = vec4(hash(vec3(c, 1.0)), hash(vec3(c, 17.0)), hash(vec3(c, 91.0)), hash(vec3(c, 213.0)));
}
`;

export const BLUENOISE_STEP_FRAG = /* glsl */`
precision highp float;
in vec2 vUv;
uniform sampler2D uSrc;
uniform vec2 uTexel;
uniform float uSigma;
layout(location = 0) out vec4 oCol;

vec4 erfApprox(vec4 x){ return tanh(1.2027 * x); }

void main(){
  vec4 c = texture(uSrc, vUv);
  vec4 mean = vec4(0.0);
  vec4 m2 = vec4(0.0);
  float wsum = 0.0;
  const int R = 3;
  for (int y = -R; y <= R; y++){
    for (int x = -R; x <= R; x++){
      float d2 = float(x * x + y * y);
      float w = exp(-d2 / (2.0 * uSigma * uSigma));
      vec4 v = texture(uSrc, vUv + vec2(float(x), float(y)) * uTexel);
      mean += v * w;
      m2 += v * v * w;
      wsum += w;
    }
  }
  mean /= wsum;
  m2 /= wsum;
  vec4 varv = max(m2 - mean * mean, vec4(1e-6));
  vec4 hp = (c - mean) / sqrt(varv);
  oCol = clamp(0.5 + 0.5 * erfApprox(hp * 0.7071), vec4(0.0015), vec4(0.9985));
}
`;
